import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Star, Quote, Heart } from 'lucide-react';

const CreatorTestimonials = () => {
  const testimonials = [
    {
      name: "Sarah M.",
      role: "Soccer Mom, Chicago",
      image: "👩‍💼",
      quote: "Finally! My phone isn't constantly full of game videos, and grandma never misses a goal. The kids love seeing themselves improve over time.",
      rating: 5,
      highlight: "Phone storage freed up",
      color: "from-blue-50 to-blue-100"
    },
    {
      name: "Mike R.",
      role: "Piano Teacher, Austin",
      image: "🎹",
      quote: "Parents love seeing their kids' progress between lessons. I've gotten more referrals in the past 3 months than the entire previous year!",
      rating: 5,
      highlight: "More referrals than ever",
      color: "from-green-50 to-green-100"
    },
    {
      name: "Jennifer K.",
      role: "Homeschool Mom, Portland",
      image: "📚",
      quote: "Our extended family feels so connected to the kids' learning journey. Even my in-laws across the country get excited about math projects now!",
      rating: 5,
      highlight: "Family stays connected",
      color: "from-purple-50 to-purple-100"
    },
    {
      name: "Emma L.",
      role: "Dance Coach, Miami",
      image: "💃",
      quote: "My private students practice so much more at home now that parents can see the routines. It's like having an extra rehearsal every day.",
      rating: 5,
      highlight: "Students practice more",
      color: "from-pink-50 to-pink-100"
    },
    {
      name: "Alex T.",
      role: "Teen Athlete, Denver",
      image: "🏀",
      quote: "I can actually see my basketball skills getting better week by week. My coach uses the videos to show me exactly what to work on.",
      rating: 5,
      highlight: "Clear progress tracking",
      color: "from-orange-50 to-orange-100"
    },
    {
      name: "Lisa P.",
      role: "Nanny, Seattle",
      image: "👶",
      quote: "The parents I work for love getting little video updates throughout the day. It helps them feel connected even when they're at work.",
      rating: 5,
      highlight: "Parents feel connected",
      color: "from-yellow-50 to-yellow-100"
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center mb-4">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-6 w-6 text-yellow-400 fill-current mx-1" />
            ))}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Loved by Families & Creators
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Real stories from real families who've transformed how they share and preserve their precious moments.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Card className={`h-full bg-gradient-to-br ${testimonial.color} border-2 border-gray-100 hover:shadow-lg transition-shadow duration-300`}>
                <CardContent className="p-6">
                  {/* Rating */}
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                    ))}
                  </div>

                  {/* Quote */}
                  <div className="relative mb-6">
                    <Quote className="h-8 w-8 text-gray-300 absolute -top-2 -left-2" />
                    <p className="text-gray-700 italic pl-6 leading-relaxed">
                      {testimonial.quote}
                    </p>
                  </div>

                  {/* Highlight */}
                  <div className="bg-white bg-opacity-70 rounded-lg p-3 mb-4">
                    <div className="flex items-center">
                      <Heart className="h-4 w-4 text-red-500 mr-2 fill-current" />
                      <span className="text-sm font-semibold text-gray-800">
                        {testimonial.highlight}
                      </span>
                    </div>
                  </div>

                  {/* Author */}
                  <div className="flex items-center">
                    <div className="text-2xl mr-3">{testimonial.image}</div>
                    <div>
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-gray-600">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-8 text-white text-center"
        >
          <h3 className="text-2xl md:text-3xl font-bold mb-8">
            Join Thousands of Happy Families
          </h3>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="text-3xl md:text-4xl font-bold mb-2">50K+</div>
              <div className="text-purple-100">Videos Shared Safely</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold mb-2">2,500+</div>
              <div className="text-purple-100">Happy Families</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold mb-2">98%</div>
              <div className="text-purple-100">Would Recommend</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold mb-2">4.9★</div>
              <div className="text-purple-100">Average Rating</div>
            </div>
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-br from-gray-50 to-purple-50 rounded-2xl p-8 max-w-2xl mx-auto border-2 border-purple-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Start Your Family's Story?
            </h3>
            <p className="text-gray-600 mb-6">
              Join thousands of families who've made the switch from crowded camera rolls and unsafe social media to organized, private video sharing.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 text-lg">
                Start Free Trial - $9/month
              </button>
              <button className="border-2 border-purple-300 text-purple-700 px-8 py-4 rounded-lg font-semibold hover:bg-purple-50 transition-all duration-300 text-lg">
                See How It Works
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              14-day free trial • No credit card required • Cancel anytime
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CreatorTestimonials;